import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ClipboardCheck,
  DoorOpen,
  FileText,
  HelpCircle,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  MessageCircle,
  RefreshCw,
  Settings,
  ShieldCheck,
  Sparkles,
  Truck,
  UserCheck,
  Users,
  Wallet,
  Wrench,
} from "lucide-react";
import { Logo } from "@/components/shared/Logo";
import { NotificationBell } from "@/components/shared/NotificationBell";
import { AvatarPill } from "@/components/shared/AvatarPill";
import { useAuth } from "@/context/AuthContext";
import { useProperty } from "@/context/PropertyContext";
import { usePendingApprovalsCount } from "@/hooks/useApprovals";
import { useCurrentUserPermissions } from "@/hooks/useTeamPermissions";
import { hasPermission } from "@/lib/permissions";
import { cn } from "@/lib/utils";
import { SkipLink } from "@/components/a11y/SkipLink";
import { MobileNav, MobileNavCloseButton } from "@/components/layout/MobileNav";
import type { PermissionKey } from "@/lib/permissions";

interface NavItem {
  to: string;
  icon: typeof BarChart3;
  label: string;
  badgeKey?: "approvals";
  /** Hidden from the staff role (admins/owners only). */
  adminOnly?: boolean;
  /** Hidden when this permission is explicitly disabled for the user. */
  permission?: PermissionKey;
}

const NAV: NavItem[] = [
  { to: "/admin/dashboard", icon: LayoutDashboard, label: "Dashboard", adminOnly: true },
  { to: "/admin/approvals", icon: UserCheck, label: "Approvals", badgeKey: "approvals", adminOnly: true },
  { to: "/admin/students", icon: Users, label: "Students" },
  { to: "/admin/payments", icon: Wallet, label: "Payments", adminOnly: true, permission: "view_financials" },
  { to: "/admin/invites", icon: KeyRound, label: "Invites", adminOnly: true },
  { to: "/admin/contracts", icon: FileText, label: "Contracts", adminOnly: true },
  { to: "/admin/renewals", icon: RefreshCw, label: "Renewals", adminOnly: true },
  { to: "/admin/rooms", icon: DoorOpen, label: "Rooms" },
  { to: "/admin/transport", icon: Truck, label: "Transport" },
  { to: "/admin/maintenance", icon: Wrench, label: "Maintenance" },
  { to: "/admin/inspections", icon: ClipboardCheck, label: "Inspections" },
  { to: "/admin/expenses", icon: BarChart3, label: "Expenses" },
];

const NSFAS_NAV: NavItem[] = [
  { to: "/admin/nsfas/payouts", icon: ShieldCheck, label: "Payouts" },
  { to: "/admin/nsfas/import", icon: Sparkles, label: "Import" },
  { to: "/admin/nsfas/export", icon: FileText, label: "Reconciliation" },
  { to: "/admin/nsfas/submission", icon: FileText, label: "Submission list" },
];

const COMM_NAV: NavItem[] = [
  { to: "/admin/calendar", icon: Calendar, label: "Calendar" },
  { to: "/admin/chat", icon: MessageCircle, label: "Chat" },
  { to: "/admin/announcements", icon: Bell, label: "Announcements" },
];

export function AdminLayout() {
  const { profile, signOut } = useAuth();
  const { property } = useProperty();
  const navigate = useNavigate();
  const approvalsCount = usePendingApprovalsCount().data ?? 0;
  const badges = { approvals: approvalsCount } as const;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: currentPerms } = useCurrentUserPermissions();

  const isStaff = profile?.role === "staff";
  const opsNav = NAV.filter(
    (i) =>
      (!i.adminOnly || !isStaff) &&
      (!i.permission || hasPermission(currentPerms, i.permission)),
  );

  const closeSidebar = () => setSidebarOpen(false);

  const sidebarContent = (showMobileClose: boolean) => (
    <>
      <div className="px-6 pt-6 pb-4 border-b border-border flex items-center justify-between">
        <Logo size="md" />
        {showMobileClose && <MobileNavCloseButton onClick={closeSidebar} />}
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6 no-scrollbar">
        <NavGroup label="Operations" items={opsNav} badges={badges} onNavigate={closeSidebar} />
        {!isStaff && (
          <NavGroup label="NSFAS" items={NSFAS_NAV} badges={badges} onNavigate={closeSidebar} />
        )}
        <NavGroup label="Communications" items={COMM_NAV} badges={badges} onNavigate={closeSidebar} />
      </nav>

      <div className="border-t border-border px-3 py-3 space-y-1">
        {!isStaff && (
          <NavLink
            to="/admin/settings"
            onClick={closeSidebar}
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
                isActive
                  ? "bg-gold-dim text-gold2"
                  : "text-muted hover:text-text hover:bg-surface2",
              )
            }
          >
            <Settings className="h-4 w-4" />
            Settings
          </NavLink>
        )}
        <NavLink
          to="/help"
          onClick={closeSidebar}
          className={({ isActive }) =>
            cn(
              "flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
              isActive
                ? "bg-gold-dim text-gold2"
                : "text-muted hover:text-text hover:bg-surface2",
            )
          }
        >
          <HelpCircle className="h-4 w-4" />
          Help
        </NavLink>
        <button
          onClick={async () => {
            await signOut();
            navigate("/login", { replace: true });
          }}
          className="w-full flex items-center gap-3 h-9 px-3 rounded-md text-sm text-muted hover:text-danger hover:bg-surface2"
        >
          <LogOut className="h-4 w-4" />
          Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex bg-black">
      <SkipLink />

      {/* Desktop sidebar — always visible at md+, hidden below */}
      <aside
        className="hidden md:flex md:static w-[228px] flex-shrink-0 border-r border-border bg-surface flex-col"
        aria-label="Main navigation"
      >
        {sidebarContent(false)}
      </aside>

      {/* Mobile off-canvas drawer */}
      <MobileNav
        id="admin-mobile-nav"
        open={sidebarOpen}
        onClose={closeSidebar}
        ariaLabel="Admin navigation"
      >
        {sidebarContent(true)}
      </MobileNav>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-surface flex items-center justify-between px-4 md:px-[30px]" style={{ paddingTop: 'env(safe-area-inset-top)', minHeight: 'calc(56px + env(safe-area-inset-top))' }}>
          <div className="flex items-center gap-3">
            {/* Hamburger — mobile only */}
            <button
              type="button"
              className="md:hidden flex items-center justify-center h-11 w-11 rounded-md bg-surface2 border border-border text-text hover:border-gold hover:text-gold transition-colors mr-1"
              onClick={() => setSidebarOpen(true)}
              aria-label="Open navigation menu"
              aria-expanded={sidebarOpen}
              aria-controls="admin-mobile-nav"
              style={{ touchAction: "manipulation", WebkitTapHighlightColor: "transparent" }}
            >
              <Menu className="h-5 w-5" />
            </button>
            {/* Back to Portfolio — owner only */}
            {profile?.role === "owner" && (
              <button
                onClick={() => navigate("/owner/dashboard")}
                className="flex items-center gap-1.5 text-xs text-muted hover:text-gold transition-colors mr-2"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Portfolio</span>
              </button>
            )}
            <Building2 className="h-4 w-4 text-gold" />
            <div className="flex items-baseline gap-2">
              <h1 className="font-display text-base font-semibold text-text">
                {property?.name ?? "M House"}
              </h1>
              <span className="hidden sm:inline text-xs text-muted">
                {property?.suburb ? `${property.suburb}, ${property.city ?? "ZA"}` : "Soshanguve Block L, Pretoria"}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <div className="flex items-center gap-2">
              <AvatarPill name={profile?.full_name ?? null} size={32} />
              <div className="text-right hidden sm:block">
                <div className="text-xs font-semibold text-text">
                  {profile?.full_name ?? "—"}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-muted">
                  {profile?.role === "owner" ? "Owner" : profile?.role === "staff" ? "Staff" : "Admin"}
                </div>
              </div>
            </div>
          </div>
        </header>

        <main
          id="main"
          className="flex-1 overflow-y-auto p-4 md:p-[26px_30px]"
          aria-hidden={sidebarOpen ? "true" : undefined}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavGroup({
  label,
  items,
  badges,
  onNavigate,
}: {
  label: string;
  items: NavItem[];
  badges: Record<NonNullable<NavItem["badgeKey"]>, number>;
  onNavigate: () => void;
}) {
  return (
    <div>
      <div className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-dim">
        {label}
      </div>
      <div className="space-y-0.5">
        {items.map((item) => {
          const count = item.badgeKey ? badges[item.badgeKey] : 0;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onNavigate}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 h-9 px-3 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-gold-dim text-gold2 font-semibold"
                    : "text-muted hover:text-text hover:bg-surface2",
                )
              }
            >
              <item.icon className="h-4 w-4" />
              <span className="flex-1">{item.label}</span>
              {count > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-danger text-white text-[10px] font-bold leading-none">
                  {count > 99 ? "99+" : count}
                </span>
              )}
            </NavLink>
          );
        })}
      </div>
    </div>
  );
}
